/**
 * Human-readable text for a trail drop-off alert tier.
 *
 * WHY THIS EXISTS
 * ---------------
 * The backend can start emitting a new alert tier before the UI knows about it:
 * the query and the SPA deploy from the same commit but through different steps
 * (functions, then the unconditional hosting deploy), so there is always a window
 * where a tier reaches the browser unmapped. That happened on prod on 2026-08-25:
 * `historical_dropoff` and `recovered_dropoff` went live in the query while the
 * SPA was still the previous build, and every such row rendered as "Unknown".
 *
 * "Unknown" is the wrong thing to render. On a regulated commission report it
 * reads to a broker as a data fault rather than a UI gap, and it throws away
 * information that is already in the row: the tier's own name. Deriving the label
 * from the value means an unmapped tier still reads sensibly, so the deploy-order
 * window stops being user-visible.
 *
 * Curated copy still wins where it exists — `humaniseAlertType` is the FLOOR, not
 * the ceiling. Callers should look up their own label map first and fall back here.
 */

/** Curated labels. Anything absent falls through to the derived form. */
const CURATED: Record<string, string> = {
  current_month_dropoff: 'Current drop-off',
  previous_months_dropoff: 'Multi-month trail gap',
  historical_dropoff: 'Earlier unresolved drop-off',
  recovered_dropoff: 'Trail resumed',
  significant_variance: 'Significant drop',
  second_year_commencement: '2nd year start',
};

/**
 * `historical_dropoff` -> `Historical dropoff`.
 *
 * Deliberately dumb: split on underscores, sentence-case the first word, leave the
 * rest lowercase. It will never be as good as curated copy, but it is always better
 * than a constant, and it needs no maintenance when a tier is added.
 */
export function humaniseAlertType(alertType: string): string {
  const words = alertType.split('_').filter(Boolean);
  if (words.length === 0) return 'Alert';
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * The label to show for a tier. Curated copy if we have it, otherwise derived from
 * the tier's own name. Only a null/empty tier yields the generic fallback, because
 * at that point there genuinely is no information to render.
 */
export function alertTypeLabel(alertType: string | null | undefined): string {
  if (!alertType) return 'Alert';
  return CURATED[alertType] ?? humaniseAlertType(alertType);
}
